import React, { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
} from "recharts";
import {
  MousePointerClick,
  Flame,
  Calendar,
  TrendingUp,
  Loader2,
  Info,
} from "lucide-react";
import {
  fetchOverview,
  fetchTimeseries,
  fetchProfitability,
} from "../../lib/adminApi";
import { Badge } from "../../components/ui/badge";

const COLORS = [
  "#06B6D4", "#F59E0B", "#A855F7", "#10B981", "#EF4444",
  "#3B82F6", "#F97316", "#EC4899", "#14B8A6", "#F43F5E",
  "#8B5CF6", "#22D3EE", "#84CC16", "#FB7185",
];

const TABS = [
  { id: "clicks", label: "Clics par fournisseur", testid: "tab-clicks" },
  { id: "profit", label: "Rentabilité estimée", testid: "tab-profit" },
];

export default function AdminDashboardPage() {
  const [overview, setOverview] = useState(null);
  const [timeseries, setTimeseries] = useState(null);
  const [profit, setProfit] = useState(null);
  const [tab, setTab] = useState("clicks");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetchOverview(),
      fetchTimeseries(30),
      fetchProfitability(30),
    ])
      .then(([ov, ts, pr]) => {
        setOverview(ov);
        setTimeseries(ts);
        setProfit(pr);
      })
      .finally(() => setLoading(false));
  }, []);

  const kpis = useMemo(() => {
    if (!overview) return [];
    return [
      { label: "Total clics", value: overview.total, icon: MousePointerClick, color: "text-cyan-300" },
      { label: "7 derniers jours", value: overview.last_7_days, icon: Flame, color: "text-amber-300" },
      { label: "30 derniers jours", value: overview.last_30_days, icon: Calendar, color: "text-emerald-300" },
      {
        label: "Commission estimée 30j",
        value: profit?.suppliers?.reduce((s, r) => s + r.estimated_commission, 0).toFixed(2) + " €",
        icon: TrendingUp,
        color: "text-fuchsia-300",
      },
    ];
  }, [overview, profit]);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center text-slate-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        Chargement des statistiques...
      </div>
    );
  }

  return (
    <div data-testid="admin-dashboard" className="space-y-6">
      <header>
        <h1 className="font-heading text-2xl font-extrabold text-slate-50 sm:text-3xl">
          Tableau de bord
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-400">
          Ce tableau mesure les{" "}
          <strong className="text-slate-200">clics sortants</strong> vers les
          fournisseurs. Les ventes confirmées et commissions réelles restent
          suivies par chaque plateforme d'affiliation (Effinity, Kwanko, Awin…).
        </p>
      </header>

      {/* KPIs */}
      <div
        data-testid="admin-kpi-grid"
        className="grid grid-cols-2 gap-3 lg:grid-cols-4"
      >
        {kpis.map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="surface-card rounded-xl p-4">
            <div className="flex items-center gap-2 text-slate-500">
              <Icon className={`h-4 w-4 ${color}`} />
              <span className="font-mono text-[10px] uppercase tracking-widest">
                {label}
              </span>
            </div>
            <div className="mt-2 font-heading text-2xl font-extrabold text-slate-50 sm:text-3xl">
              {value}
            </div>
          </div>
        ))}
      </div>

      {/* Timeseries */}
      <section className="surface-card rounded-2xl p-5 sm:p-6">
        <h2 className="mb-4 font-heading text-lg font-bold text-slate-50">
          Évolution des clics (30 derniers jours)
        </h2>
        <div className="h-56 w-full" data-testid="admin-timeseries-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={timeseries?.series || []}>
              <CartesianGrid stroke="rgba(100,116,139,0.15)" vertical={false} />
              <XAxis
                dataKey="date"
                stroke="#64748b"
                fontSize={10}
                tickFormatter={(v) => v.slice(5)}
              />
              <YAxis stroke="#64748b" fontSize={10} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  background: "#0f172a",
                  border: "1px solid #334155",
                  borderRadius: 8,
                }}
                labelStyle={{ color: "#cbd5e1" }}
              />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#06B6D4"
                strokeWidth={2}
                dot={{ fill: "#06B6D4", r: 2 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Tabs */}
      <section className="surface-card rounded-2xl p-5 sm:p-6">
        <div
          data-testid="admin-tabs"
          className="mb-5 inline-flex items-center gap-1 rounded-full border border-slate-800 bg-slate-900/70 p-1"
        >
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              data-testid={t.testid}
              onClick={() => setTab(t.id)}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition ${
                tab === t.id
                  ? "bg-cyan-500 text-slate-950"
                  : "text-slate-300 hover:text-cyan-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "clicks" ? (
          <ClicksView overview={overview} />
        ) : (
          <ProfitView profit={profit} />
        )}
      </section>
    </div>
  );
}

const ClicksView = ({ overview }) => {
  const data = (overview?.by_supplier || []).map((d, i) => ({
    name: d.key,
    count: d.count,
    color: COLORS[i % COLORS.length],
  }));
  if (data.length === 0) {
    return (
      <p className="text-sm text-slate-400">
        Aucun clic enregistré pour le moment. Les clics sortants apparaîtront ici
        dès que des visiteurs utiliseront le comparateur.
      </p>
    );
  }
  return (
    <div className="grid gap-6 lg:grid-cols-5" data-testid="clicks-view">
      <div className="h-72 lg:col-span-3">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 20 }}>
            <CartesianGrid stroke="rgba(100,116,139,0.15)" vertical={false} />
            <XAxis dataKey="name" stroke="#64748b" fontSize={10} angle={-25} textAnchor="end" height={60} />
            <YAxis stroke="#64748b" fontSize={10} allowDecimals={false} />
            <Tooltip
              contentStyle={{
                background: "#0f172a",
                border: "1px solid #334155",
                borderRadius: 8,
              }}
              labelStyle={{ color: "#cbd5e1" }}
            />
            <Bar dataKey="count" radius={[6, 6, 0, 0]}>
              {data.map((d, i) => (
                <Cell key={i} fill={d.color} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="h-72 lg:col-span-2">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="count" nameKey="name" outerRadius={90} innerRadius={55} paddingAngle={2}>
              {data.map((d, i) => (
                <Cell key={i} fill={d.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                background: "#0f172a",
                border: "1px solid #334155",
                borderRadius: 8,
              }}
            />
            <Legend wrapperStyle={{ fontSize: 11, color: "#cbd5e1" }} />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const ProfitView = ({ profit }) => {
  const rows = profit?.suppliers || [];
  const hasData = rows.some((r) => r.commission_rate > 0 && r.panier_moyen > 0);
  return (
    <div data-testid="profit-view" className="space-y-4">
      <div className="flex items-start gap-2 rounded-xl border border-cyan-500/30 bg-cyan-500/5 p-3 text-xs text-cyan-100">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-cyan-300" />
        <div>
          Formule :{" "}
          <code className="rounded bg-slate-900 px-1.5 py-0.5 font-mono">
            clics × panier moyen × 5 % (conversion) × taux %
          </code>
          . Pour ajuster les taux et paniers moyens, va dans l'onglet{" "}
          <strong className="text-cyan-200">Fournisseurs</strong>.
        </div>
      </div>

      {!hasData && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/5 p-4 text-sm text-amber-200">
          Renseigne au moins un <strong>taux de commission</strong> et un{" "}
          <strong>panier moyen</strong> dans l'onglet « Fournisseurs » pour voir
          apparaître la rentabilité estimée.
        </div>
      )}

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} layout="vertical" margin={{ top: 10, right: 20, left: 90, bottom: 10 }}>
            <CartesianGrid stroke="rgba(100,116,139,0.15)" horizontal={false} />
            <XAxis type="number" stroke="#64748b" fontSize={10} />
            <YAxis dataKey="supplier_name" type="category" stroke="#64748b" fontSize={11} width={100} />
            <Tooltip
              contentStyle={{
                background: "#0f172a",
                border: "1px solid #334155",
                borderRadius: 8,
              }}
              labelStyle={{ color: "#cbd5e1" }}
              formatter={(v) => `${v.toFixed(2)} €`}
            />
            <Bar dataKey="estimated_commission" radius={[0, 6, 6, 0]}>
              {rows.map((r, i) => (
                <Cell key={i} fill={r.couleur || COLORS[i % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid gap-2 text-xs">
        {rows.slice(0, 6).map((r) => (
          <div
            key={r.supplier_id}
            className="flex items-center justify-between rounded-md border border-slate-800 bg-slate-900/40 px-3 py-2"
          >
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full" style={{ background: r.couleur }} />
              <span className="font-semibold text-slate-200">{r.supplier_name}</span>
            </div>
            <div className="flex items-center gap-3 text-slate-400">
              <span>{r.clicks} clics</span>
              <Badge
                variant="outline"
                className="border-slate-700 bg-slate-900/70 text-[10px] text-slate-300"
              >
                {r.commission_rate}% · {r.panier_moyen}€
              </Badge>
              <span className="font-mono font-bold text-cyan-300">
                {r.estimated_commission.toFixed(2)} €
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
